import React, { createContext, useContext, ReactNode } from 'react';
import { Exercise } from '../Clients/reminds';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClientContext } from './ApiClient.Context';

interface ContextType {
  data: Exercise[];
  addExerciseType: (exercise: Exercise) => void;
  deleteExerciseType: (id: number) => void;
  updateExerciseType: (id: number, exercise: Exercise) => void;
}

const Context = createContext<ContextType | undefined>(undefined);

export const useExerciseTypesContext = () => {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useExerciseTypesContext must be used within an ExerciseTypesProvider');
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const ExerciseTypesProvider: React.FC<Props> = ({ children }) => {
  const { exercisesClient } = useApiClientContext();
  const queryClient = useQueryClient();

  const { data = [] } = useQuery({
    queryKey: ['exerciseTypes'],
    queryFn: () => exercisesClient.getExercises(),
  });

  const { mutate: addExerciseType } = useMutation({
    mutationFn: (exercise: Exercise) => exercisesClient.createExercise(exercise),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exerciseTypes'] });
    },
    onError: error => {
      console.error('Error adding exercise type:', error);
    },
  });

  const { mutate: deleteExerciseType } = useMutation({
    mutationFn: (id: number) => exercisesClient.deleteExercise(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exerciseTypes'] });
    },
    onError: error => {
      console.error('Error deleting exercise type:', error);
    },
  });

  const { mutate: updateExerciseType } = useMutation({
    mutationFn: ({ id, exercise }: { id: number; exercise: Exercise }) => exercisesClient.updateExercise(id, exercise),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exerciseTypes'] });
    },
    onError: error => {
      console.error('Error updating exercise type:', error);
    },
  });

  return (
    <Context.Provider
      value={{
        data: data ?? [],
        addExerciseType,
        deleteExerciseType,
        updateExerciseType: (id, exercise) => updateExerciseType({ id, exercise }),
      }}
    >
      {children}
    </Context.Provider>
  );
};
